/**
 * E2E-only entry to the onboarding gate's Magic Moment screen.
 *
 * The real path to Magic Moment needs a new account and a camera that passes
 * the connection test, neither of which the e2e lanes have. The probe places
 * the real gate on that step for a stand-in device; the screen, its playback
 * sampler, its timers and its telemetry stay untouched, and the test supplies
 * the video the screen samples.
 *
 * Per rule: regression-tests-for-state-machine-primitives (ai.knowledge/global)
 * — the real gate and screen, not a mock of either.
 */

export interface OnboardingMagicMomentTarget {
  readonly deviceId: string;
  readonly name: string;
  readonly host: string;
}

interface OnboardingMagicMomentProbe {
  open(target: OnboardingMagicMomentTarget): void;
}

declare global {
  var __irisOnboardingMagicMomentProbe: OnboardingMagicMomentProbe | undefined;
}

const isE2eEnabled = (): boolean =>
  globalThis.window?.electron?.process?.env?.IRIS_E2E_TEST === '1';

let openHandler: ((target: OnboardingMagicMomentTarget) => void) | undefined;

/** The gate stays real; this only exposes its Magic Moment step to E2E. */
export const bindOnboardingMagicMomentOpen = (
  handler: (target: OnboardingMagicMomentTarget) => void
): (() => void) => {
  if (!isE2eEnabled()) return () => undefined;

  openHandler = handler;
  return () => {
    if (openHandler === handler) openHandler = undefined;
  };
};

if (isE2eEnabled()) {
  const probe: OnboardingMagicMomentProbe = {
    open(target): void {
      if (openHandler === undefined) throw new Error('Onboarding Magic Moment probe is not bound');
      openHandler(target);
    }
  };

  globalThis.__irisOnboardingMagicMomentProbe = probe;
}
